// The combat substrate's shared half: the words that cross the wire and
// the few constants both sides genuinely need. Every damage number, hit
// probability, and integrity value is SERVER-ONLY (combat/catalog.ts) on
// the same terms as wear and charge — what replicates is a word, and the
// instrument reads analog.
//
// The enemy of this wave is the WARDEN: a rogue automaton of the old
// wars, still holding a perimeter nobody remembers, deep in the Black.
// Wardens are not ships and not equipment — nobody owns one, nobody
// pilots one, and disabling one mints nothing (earnable-only: a cleared
// road is the reward). Near-planet orbit is safe; the deep Black is
// theirs — danger keys on PLACE, exactly as the Edge does on the ground.

/** Inside this radius of the sub-settlement point the Black is safe:
 *  no warden posts, no aggro. The commit shelf and arrival lanes all sit
 *  well inside it. */
export const SAFE_ORBIT_R_M = 3000;

/** Coil-gun reach, metres. Client uses it to cap the tracer/reticle ray;
 *  the server enforces it in resolution. */
export const WEAPON_RANGE_M = 700;

/** Minimum interval between coil shots (hold-to-fire cadence). */
export const FIRE_COOLDOWN_MS = 250;

/** Warden disposition words — the whole player-facing surface of what a
 *  warden is doing. */
export const WARDEN_STATES = ["dormant", "hunting", "adrift"] as const;
export type WardenStateWord = (typeof WARDEN_STATES)[number];

/** Warden integrity as words, best to worst. The worst is `adrift`, and
 *  adrift is a state it RECOVERS from — wardens re-knit; the deep Black
 *  never runs out of danger and never drops loot. */
export const WARDEN_BANDS = ["whole", "scored", "riven", "adrift"] as const;
export type WardenBand = (typeof WARDEN_BANDS)[number];

export function asWardenState(value: string): WardenStateWord {
  return (WARDEN_STATES as readonly string[]).includes(value)
    ? (value as WardenStateWord)
    : "dormant";
}

export function asWardenBand(value: string): WardenBand {
  return (WARDEN_BANDS as readonly string[]).includes(value) ? (value as WardenBand) : "whole";
}
